import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '~/contexts/newToastContext';
import { UpgradeModule } from '~/pages/NewOverview/components/UpgradeModule';
import { useFilterContext } from '~/pages/NewOverview/contexts/FilterContext';
import { getWaterOverviewInfo } from '~/pages/NewOverview/helpers/overview';
import { WaterOverviewInfo } from '~/providers/types/api-private';
import { WaterContainer, WaterContainerLoading } from '../styles';
import { WaterCardContent } from './CardContent';

interface WaterCardProps {
  expanded: boolean;
  loading: boolean;
  order: number;
  hasWater: boolean;
}

export function WaterCard({
  expanded,
  hasWater,
  order,
  loading,
}: Readonly<WaterCardProps>): ReactElement {
  const { clients, selectedOptions, filtersDependencies } = useFilterContext();
  const [loadingData, setLoadingData] = useState(false);
  const [waterData, setWaterData] = useState<WaterOverviewInfo>();
  const { toast } = useToast();
  const { t } = useTranslation();

  const generalLoading = useMemo(() => loading || loadingData, [loading, loadingData]);

  const getWaterInfo = useCallback(async (): Promise<void> => {
    setLoadingData(true);

    const clientIds = clients.map(Number);
    const data = await getWaterOverviewInfo(clientIds, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      setWaterData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosAgua'), 'error');
    }
    setLoadingData(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (clients && clients.length > 0 && hasWater && !loading) {
      getWaterInfo();
    }
  }, [getWaterInfo, hasWater, loading, ...filtersDependencies]);

  useEffect(() => {
    if (!hasWater || loading) {
      setWaterData(undefined);
      setLoadingData(false);
    }
  }, [hasWater, loading]);

  if (generalLoading) {
    return (
      <WaterContainerLoading style={{ order }}>
        <Skeleton
          height="360px"
          borderRadius="8px"
          className="skeleton-span"
          containerClassName="skeleton-container-span"
        />
      </WaterContainerLoading>
    );
  }

  return (
    <WaterContainer
      expanded={expanded}
      style={{ order }}
    >
      {!hasWater && !loading && (<UpgradeModule module="moreModules" />)}
      <WaterCardContent data={hasWater ? waterData : undefined} expanded={expanded} />
    </WaterContainer>
  );
}
